import { useEffect, useRef } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

/**
 * Visualiseur de l'icosaèdre — LE BACKEND POSSÈDE LA FORME ET LE MOUVEMENT.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
 *
 * ⚠️ Ce hook construisait auparavant sa PROPRE scène, sans rapport avec celle
 * que le backend servait :
 *   - une enveloppe `IcosahedronGeometry(1.5, 1)` rigide, placée et animée avec
 *     des vitesses tirées au `Math.random()` ;
 *   - 50 particules aux positions et vitesses également tirées au hasard,
 *     confinées par un rayon fixe de 1.4 ;
 *   - et il envoyait ces vitesses fabriquées à `/dyn_entropy` comme si elles
 *     avaient été mesurées.
 * Pendant ce temps `/initial` servait un icosaèdre nu de 12 sommets, que
 * personne ne regardait, et le tick serveur simulait un troisième objet.
 *
 * Arbitrage produit du 2026-07-28, option C : la forme canonique est un objet à
 * DEUX COUCHES — enveloppe icosaédrique de 72 sommets déformée par le champ de
 * Lorenz, et 50 particules internes qu'elle confine. Le backend la génère, la
 * fait vivre, et la sert. Le front la rend.
 */
export default function useIcosahedronVisualizer({
  running = true,
  chaosMode = false,
  glow = true,
  // Repli aligné sur le défaut de la page (50 %), et non plus sur 15 % : un
  // repli qui diffère du réglage réel donne un comportement différent selon
  // que la propriété est transmise ou non.
  maxSpeedEnvPct = 50,
  maxSpeedCorePct = 50,
  pulseSpeedPct = 15,
  flashRatePct = 15,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // --- Scene ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(60, 480 / 320, 0.1, 1000);
    camera.position.set(0, 0, 6);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(480, 320, false);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    const dir = new THREE.DirectionalLight(0xffffff, 1);
    dir.position.set(5, 8, 5);
    scene.add(ambient, dir);

    // Manipulation souris — contrôles de CAMÉRA : ils déplacent le point de
    // vue, jamais la scène. Le snapshot lit l'espace objet, il n'en dépend pas.
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // --- Scène reçue du backend ---
    let envelopeMesh = null;
    let envelopeGeo = null;
    const particles = [];
    const particleColors = [];
    let velocities = [];        // MESURÉES, dérivées du déplacement réel

    // Lecture CONTINUE — voir docs/SPEC_CONTINUATION.md. Le rejeu local d'un
    // segment unique téléportait la scène à la couture (34x le pas typique).
    let lecteur = null;
    let frameDt = 0.01;   // conservé : les vitesses sont mesurées sur ce pas
    let playhead = 0;

    const controller = new AbortController();

    fetch("/api/geometry/icosahedron/initial", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!Array.isArray(data.vertices) || !Array.isArray(data.faces)) {
          throw new Error("réponse /initial invalide");
        }

        // --- Couche 1 : enveloppe, construite depuis les sommets canoniques ---
        envelopeGeo = new THREE.BufferGeometry();
        const positions = new Float32Array(data.vertices.length * 3);
        data.vertices.forEach((v, i) => {
          positions[i * 3] = v[0];
          positions[i * 3 + 1] = v[1];
          positions[i * 3 + 2] = v[2];
        });
        envelopeGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        envelopeGeo.setIndex(data.faces.flat());
        envelopeGeo.computeVertexNormals();

        envelopeMesh = new THREE.Mesh(
          envelopeGeo,
          new THREE.MeshPhongMaterial({
            color: 0x8a2be2,
            wireframe: true,
            transparent: true,
            opacity: 0.5,
            emissive: new THREE.Color(0x000000),
            emissiveIntensity: 0,
          })
        );
        scene.add(envelopeMesh);

        // --- Couche 2 : particules, aux positions et couleurs canoniques ---
        (data.particles || []).forEach((p) => {
          const mesh = new THREE.Mesh(
            new THREE.SphereGeometry(p.radius || 0.07, 12, 12),
            new THREE.MeshPhongMaterial({ color: new THREE.Color(...p.color) })
          );
          mesh.position.set(...p.position);
          scene.add(mesh);
          particles.push(mesh);
          particleColors.push(p.color);
          velocities.push(new THREE.Vector3(0, 0, 0));
        });

        // --- Mouvement : frames servies par le moteur ---
        // Les curseurs paramètrent le MOTEUR (règle 2), pas le rejeu :
        // `maxSpeedEnvPct` règle le pas d'intégration de l'enveloppe,
        // `maxSpeedCorePct` la vitesse initiale des particules.
        //
        // ⚠️ CORRESPONDANCE RECALÉE le 2026-07-29, sur mesure.
        //
        // L'ancienne (`dt = 0.004 + pct*0.02`, `speed = 0.2 + pct*2.0`) plaçait
        // le réglage par défaut de la page — 50 % — à dt = 0,014 et vitesse
        // 1,2 : les particules ne rebondissaient que 4 fois sur toute la
        // boucle, et le mouvement paraissait figé. Balayage sur la séquence
        // réellement servie (240 pas) :
        //
        //     dt      vitesse   boucle   rebonds/particule   cohérence
        //   0,010       1,0      2,40 s          2             0,389
        //   0,015       2,5      3,60 s          5             0,313
        //   0,020       1,0      4,80 s          6             0,282
        //   0,020       2,5      4,80 s          8             0,223   ← retenu
        //   0,020       4,0      4,80 s          9             0,250
        //
        // La cohérence directionnelle vaut 1 pour une translation pure et 0
        // pour un rebond parfait : c'est elle qu'on minimise, pas le nombre de
        // rebonds seul. Le réglage retenu place le défaut de la page (50 %) sur
        // l'optimum, et laisse la course entière du curseur exploitable.
        const params = new URLSearchParams({
          steps: "240",
          dt: String(0.010 + (maxSpeedEnvPct / 100) * 0.020),
          particle_speed: String(0.5 + (maxSpeedCorePct / 100) * 4.0),
        });
        lecteur = creerLecteurContinu({
          url: "/api/geometry/icosahedron/animate",
          parametres: () => Object.fromEntries(params),
          onSegment: (segment) => { frameDt = segment.dt; },
        });
        lecteur.demarrer();
        return null;
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erreur scène icosahedron:", err);
        }
      });

    // --- Snapshot : MESURÉ, jamais fabriqué ---
    canvas.__oracleSnapshot = () => ({
      positions: particles.map((p) => p.position.toArray()),
      colors: particleColors,
      velocities: velocities.map((v) => v.toArray()),
    });

    // --- Animate loop ---
    let prev = performance.now();
    let t = 0;

    function animate() {
      requestAnimationFrame(animate);
      const now = performance.now();
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;
      t += dt;

      if (running && lecteur) {
        // Rejeu : aucune position n'est calculée ici.
        playhead += dt;
        const vue = lecteur.lire(playhead);
        if (!vue) return;
        if (vue.bascule || vue.gel) playhead = 0;
        const al = vue.alpha;
        const A = vue.a;
        const B = vue.b;

        // Couche 1 — sommets de l'enveloppe déformée.
        if (envelopeGeo && A.vertices && B.vertices) {
          const attr = envelopeGeo.getAttribute("position");
          for (let i = 0; i < A.vertices.length && i < attr.count; i++) {
            const a = A.vertices[i], b = B.vertices[i];
            attr.setXYZ(
              i,
              a[0] + (b[0] - a[0]) * al,
              a[1] + (b[1] - a[1]) * al,
              a[2] + (b[2] - a[2]) * al
            );
          }
          attr.needsUpdate = true;
          envelopeGeo.computeVertexNormals();
        }

        // Couche 2 — particules, et vitesses MESURÉES sur le pas servi.
        if (A.particles && B.particles) {
          particles.forEach((mesh, i) => {
            const a = A.particles[i], b = B.particles[i];
            if (!a || !b) return;
            mesh.position.set(
              a[0] + (b[0] - a[0]) * al,
              a[1] + (b[1] - a[1]) * al,
              a[2] + (b[2] - a[2]) * al
            );
            velocities[i].set(
              (b[0] - a[0]) / frameDt,
              (b[1] - a[1]) / frameDt,
              (b[2] - a[2]) / frameDt
            );
          });
        }
      }

      // --- Effets de RENDU : ils colorent et pulsent, ils ne déplacent rien.
      // Ils n'entrent donc dans aucun observable.
      if (envelopeMesh) {
        if (glow) {
          envelopeMesh.material.color.setHSL((Math.sin(t * 0.4) + 1) / 2, 0.6, 0.5);
        }
        if (flashRatePct > 0) {
          const flash = 0.5 + 0.5 * Math.sin(t * 10 * (flashRatePct / 100));
          envelopeMesh.material.emissive = new THREE.Color(0xffd600);
          envelopeMesh.material.emissiveIntensity = flash * (flashRatePct / 100) * 2.0;
        } else {
          envelopeMesh.material.emissiveIntensity = 0;
        }
        if (pulseSpeedPct > 0) {
          const s = 1 + 0.05 * Math.sin(t * 2 * (pulseSpeedPct / 100));
          envelopeMesh.scale.set(s, s, s);
        }
      }
      if (chaosMode) {
        particles.forEach((ball, i) => {
          ball.material.color.setHSL((t * 0.2 + i * 0.05) % 1, 0.8, 0.5);
        });
      }

      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    return () => {
      controller.abort();
      if (lecteur) lecteur.arreter();
      delete canvas.__oracleSnapshot;
      controls.dispose();
      renderer.dispose();
    };
  }, [running, chaosMode, glow, maxSpeedEnvPct, maxSpeedCorePct, pulseSpeedPct, flashRatePct]);

  return canvasRef;
}
